import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { omit } from 'lodash-es';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { notificationTypeEnum } from 'src/account/my-account/ui/my-account.page';
import {
  AccountFormFields,
  accountFormFieldsSchema,
} from 'src/account/shared/ui/account-form-fields';
import { Loading } from 'src/shared/ui/components';
import { Alert, Button, Modal } from 'src/shared/ui/elements';
import * as Format from 'src/shared/utils/format';
import { handleApiError } from 'src/shared/utils/handleApiError';
import * as Yup from 'yup';
import { UPDATE_ACCOUNT } from '../graphql/update-account.mutation';
import { useAccountInfo } from '../service/account.hooks';
import { useDeepCompareEffect } from 'use-deep-compare';

const schema = Yup.object().shape(accountFormFieldsSchema);

export const AccountModal = ({ $account, $actions, $refetch }) => {
  const { data: accountInfo, loading: accountDataLoading } = useAccountInfo();

  const affiliations = React.useMemo(() => {
    return accountInfo?.account?.affiliations || [];
  }, [accountInfo]);

  // Reorganize subscriptions by franchise
  const subscriptionsByFranchiseId = React.useMemo(() => {
    return $account?.subscriptions?.reduce((acc, subscription) => {
      if (!subscription.franchiseId) return acc;
      acc[subscription.franchiseId] = acc[subscription.franchiseId] || [];
      acc[subscription.franchiseId] = [
        ...acc[subscription.franchiseId],
        subscription,
      ];
      return acc;
    }, {});
  }, [$account?.subscriptions]);
  const globalSubscriptionWithNullFranchiseId = React.useMemo(() => {
    const globalNewsOrAnnouncement = $account?.subscriptions?.find(
      (subscription) =>
        !subscription.franchiseId &&
        subscription.notificationId === notificationTypeEnum.newsOrAnnouncement,
    );
    const globalClassNotification = $account?.subscriptions?.find(
      (subscription) =>
        !subscription.franchiseId &&
        subscription.notificationId === notificationTypeEnum.classNotification,
    );
    if (!globalNewsOrAnnouncement?.id && !globalClassNotification?.id) {
      return null;
    }
    return {
      classNotification: globalClassNotification?.isSmsSubscribedYau,
      emailNewsOrAnnouncement: globalNewsOrAnnouncement?.isEmailSubscribed,
      newsOrAnnouncement: globalNewsOrAnnouncement?.isSmsSubscribedYau,
      emailClassNotification: true,
    };
  }, [$account?.subscriptions]);

  // Prepare franchisesOptIn array for useFieldArray
  const franchisesOptIn = React.useMemo(() => {
    return Object.keys(subscriptionsByFranchiseId)
      .map((franchiseId) => {
        const franchiseSubscription = subscriptionsByFranchiseId[franchiseId];
        const newsOrAnnouncement = franchiseSubscription?.find(
          (e) => e.notificationId === notificationTypeEnum.newsOrAnnouncement,
        );
        const classNotification = franchiseSubscription?.find(
          (e) => e.notificationId === notificationTypeEnum.classNotification,
        );
        const franchiseName = affiliations.find(
          ({ id }) => +id === +franchiseId,
        )?.name;

        if (!franchiseName) {
          return null;
        }

        return {
          id: franchiseId,
          name: franchiseName,
          classNotification: Boolean(classNotification?.isSmsSubscribedYau),
          emailClassNotification: true,
          newsOrAnnouncement: Boolean(newsOrAnnouncement?.isSmsSubscribedYau),
          emailNewsOrAnnouncement: Boolean(
            newsOrAnnouncement?.isEmailSubscribed,
          ),
        };
      })
      .filter(Boolean);
  }, [subscriptionsByFranchiseId, affiliations]);

  // Calculate global notification states based on franchises
  const globalNotificationStates = React.useMemo(() => {
    if (!franchisesOptIn.length) {
      return globalSubscriptionWithNullFranchiseId;
    }

    return {
      classNotification: franchisesOptIn.every((f) => f.classNotification),
      emailClassNotification: true,
      newsOrAnnouncement: franchisesOptIn.every((f) => f.newsOrAnnouncement),
      emailNewsOrAnnouncement: franchisesOptIn.every(
        (f) => f.emailNewsOrAnnouncement,
      ),
    };
  }, [franchisesOptIn, globalSubscriptionWithNullFranchiseId]);

  // Initialize form with default values
  const defaultValues = React.useMemo(
    () => ({
      firstName: $account.firstName,
      lastName: $account.lastName,
      phone: Format.phone($account.phone),
      email: $account.email,
      ...globalNotificationStates,
      franchisesOptIn,
    }),
    [
      $account.firstName,
      $account.lastName,
      $account.phone,
      $account.email,
      globalNotificationStates,
      franchisesOptIn,
    ],
  );

  const formMethods = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  useDeepCompareEffect(() => {
    formMethods.reset({
      ...defaultValues,
      franchisesOptIn: defaultValues.franchisesOptIn,
    });
  }, [defaultValues, formMethods]);

  const { handleSubmit, setError, formState } = formMethods;
  const [mutate, { loading }] = useMutation(UPDATE_ACCOUNT);

  const onSubmit = async (values) => {
    const formattedValues = {
      ...values,
      phone: Format.plainPhone(values.phone),
      franchisesOptIn:
        values.franchisesOptIn?.length > 0
          ? values.franchisesOptIn.map((franchise) => ({
              id: franchise.id,
              classNotification: franchise.classNotification,
              newsOrAnnouncement: franchise.newsOrAnnouncement,
              emailNewsOrAnnouncement: franchise.emailNewsOrAnnouncement,
              emailClassNotification: true,
            }))
          : [],
    };

    try {
      await mutate({
        variables: {
          input: {
            id: $account.id,
            ...omit(formattedValues, ['password', 'passwordConfirmation']),
          },
        },
      });
      await $refetch();

      setTimeout(() => {
        $actions.close();
      });
    } catch (error) {
      handleApiError(error, setError);
    }
  };

  return (
    <Modal $title="Edit account" $actions={$actions}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          {formState?.errors?.root?.serverError && (
            <Alert className="mb-16 level-error">
              {formState.errors.root.serverError.message}
            </Alert>
          )}
          <FormProvider {...formMethods}>
            <div className="tw-max-h-[65vh] tw-overflow-auto">
              <AccountFormFields isEdit={true} />
            </div>
          </FormProvider>
        </Modal.Content>
        {(loading || accountDataLoading) && <Loading className="absolute" />}
        <Modal.Footer className="flex align-center justify-flex-end">
          <Button
            className="alt outline"
            onClick={$actions.close}
            type="button"
          >
            Cancel
          </Button>

          <Button
            className="ml-12"
            type="submit"
            $loading={loading}
            $loadingText="Saving..."
          >
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
